import type { TokenCredential } from "@azure/identity";
import { authedJsonResult } from "./http.js";
import type { ProbeResult } from "./data-plane-ready.js";

const FOUNDRY_SCOPE = "https://ai.azure.com/.default";

/** A cheap data-plane GET (list agents). 200 => ready; 404/5xx => not-ready (classified upstream).
 *
 * `timeoutMs` is passed through so a POLLING caller can state a real per-attempt
 * ceiling — without one, its wall-clock bound is attempts x interval plus an
 * unbounded remainder, which is how a 5-minute budget measured 34 minutes. */
export function makeAgentsProbe(credential: TokenCredential, endpoint: string, timeoutMs?: number): () => Promise<ProbeResult> {
  return async () => {
    try {
      const r = await authedJsonResult<{ error?: { message?: string } }>({
        credential,
        scope: FOUNDRY_SCOPE,
        method: "GET",
        url: `${endpoint}/agents?api-version=v1`,
        okStatuses: [404, 500, 502, 503], // don't throw — return the status so the loop classifies it
        ...(timeoutMs !== undefined ? { timeoutMs } : {}),
      });
      if (r.status === 200) return { ok: true, status: 200, message: "" };
      const body = r.data?.error?.message ?? "";
      return { ok: false, status: r.status, message: `${r.status.toString()} ${body}`.trim() };
    } catch (e) {
      return { ok: false, message: describeProbeFailure(e) };
    }
  };
}

/**
 * Name a probe failure in terms the classifier can act on — WITHOUT laundering a
 * terminal fault into a retry.
 *
 * TWO shapes are renamed, each matched on its own SIGNATURE rather than on its
 * error class, and that distinction is the whole of this function:
 *
 *  1. The token subprocess hitting its ceiling. `az` is killed, writes nothing,
 *     and the empty stdout fails to parse — surfacing as
 *     `CredentialUnavailableError: Unexpected end of JSON input` (traced in
 *     @azure/identity 4.13.1: getAzureCliAccessToken resolves with empty stdout,
 *     getToken then JSON.parses it). That text matches no transient pattern, so
 *     it classifies `unknown`, which the readiness loop treats as a REFUSAL —
 *     pointing an operator at auth for what was a slow `az`.
 *
 *  2. undici's connection failure, which is `TypeError: fetch failed` with the
 *     real reason on `.cause`. The outer message matches nothing, so a single
 *     connection blip classified `unknown` and ABORTED the poll as a refusal.
 *
 * 🔴 MATCHED ON THE PARSE FAILURE, NOT ON `CredentialUnavailableError`. That
 * class also covers `az` missing, a claims challenge, and NOT BEING LOGGED IN —
 * all terminal. Renaming by class would make a lapsed login retry silently until
 * the budget ran out and then report an exhausted wait, which is the exact
 * "indistinguishable from broken" outcome this file exists to prevent. A
 * terminal credential fault must keep classifying non-retryable.
 *
 * RESIDUAL, stated rather than left to be discovered: if `az` writes to stderr
 * before it is killed, that timeout is NOT renamed and classifies `unknown` — a
 * bounded wait again reported as a refusal. (Traced in @azure/identity 4.13.1,
 * azureCliCredential.js: getToken's parse catch does
 * `if (obj.stderr) throw new CredentialUnavailableError(obj.stderr)` before the
 * parse error can reach the outer wrap.) Narrower and safer than the reverse
 * error — a terminal fault retried forever — which is why the match sits on this
 * side of the line; it is not free.
 */
function describeProbeFailure(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  const name = e instanceof Error ? e.name : "";
  if (/credentialunavailable/i.test(name) && /unexpected end of json input/i.test(msg)) {
    return `token acquisition timed out: ${msg}`;
  }
  // undici hides the reason on `.cause`; without it the classifier sees only
  // "fetch failed" and calls a network blip a refusal.
  if (e instanceof Error && /^fetch failed$/i.test(msg)) {
    // Only an Error cause is read. undici sets one (measured: a refused connection
    // on Node 22 gives `TypeError: fetch failed` with `cause` an Error carrying
    // `connect ECONNREFUSED …`). Anything else is dropped rather than stringified:
    // a non-Error object becomes "[object Object]", which tells a reader nothing
    // and a classifier less.
    //
    // THE WORD "network" CARRIES BOTH ARMS, not just the causeless one: it is what
    // makes classifyFoundryError return `transient`. Some causes match its regex
    // unaided (`ECONNREFUSED`) and some do not (`getaddrinfo ENOTFOUND`, `other
    // side closed`, `terminated`) — for those, and for a causeless failure, this
    // prefix is the whole of the classification. Without it they read `unknown`,
    // and the poll aborts the install as a refusal.
    const cause = (e as { cause?: unknown }).cause;
    const causeMsg = cause instanceof Error ? cause.message : "";
    return causeMsg ? `network request failed: ${causeMsg}` : "network request failed";
  }
  return msg;
}

/** A cheap per-agent data-plane GET (lookup by name). 200 => queryable; 404/5xx => not-yet (classified upstream). */
export function makeAgentProbe(credential: TokenCredential, endpoint: string, agentName: string): () => Promise<ProbeResult> {
  return async () => {
    try {
      const r = await authedJsonResult<{ error?: { message?: string } }>({
        credential,
        scope: FOUNDRY_SCOPE,
        method: "GET",
        url: `${endpoint}/agents/${agentName}?api-version=v1`,
        okStatuses: [404, 500, 502, 503], // don't throw — return the status so the loop classifies it
      });
      if (r.status === 200) return { ok: true, status: 200, message: "" };
      const body = r.data?.error?.message ?? "";
      return { ok: false, status: r.status, message: `${r.status.toString()} ${body}`.trim() };
    } catch (e) {
      return { ok: false, message: describeProbeFailure(e) };
    }
  };
}
